// Who is in a space, for the director and admins (SSettings, Spaces, Members): everyone in it with their role in the
// firm and their role in the space (Lead or Member; partners only read, so they are members), Remove after a plain
// warning, and Add person: anyone active, of any role. The same sheet on a phone. Changes save at once.
import { useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { can, type Member, type Role, type SessionUser } from '@dpw/shared';
import { api } from '../api';
import { useChange, useSpace, useUsers } from '../lib/queries';
import { usePhone } from '../lib/media';
import { Avatar, ErrorNote } from './bits';
import { Sheet } from './Sheet';
import { useToast } from './toast';
import { PSheet } from '../phone/kit';

export const ROLE_WORD: Record<Role, string> = { partner: 'Partner', director: 'Director', member: 'Member' };
/** Admins and the director manage who is in every space; a space's lead manages theirs (5 Nov). */
export const managesMembers = (me: SessionUser, space?: { members: Member[] }) =>
  me.isAdmin || can(me.role, 'space.all') || !!space?.members.some(m => m.id === me.id && m.role === 'lead');

export function MembersSheet({ spaceId, onClose }: { spaceId: string; onClose: () => void }) {
  const phone = usePhone();
  const space = useSpace(spaceId);
  const users = useUsers();
  const qc = useQueryClient();
  const toast = useToast();
  const [who, setWho] = useState('');
  const [role, setRole] = useState<'lead' | 'member'>('member');
  const [asking, setAsking] = useState<string | null>(null);
  const done = () => { void qc.invalidateQueries(); };
  const add = useChange((x: { userId: string; role: 'lead' | 'member' }) => api.spaces.addMember(spaceId, x.userId, x.role));
  const remove = useChange((userId: string) => api.spaces.removeMember(spaceId, userId));
  const s = space.data;
  const name = s?.name ?? 'this space';
  const members = s?.members ?? [];
  const inSpace = new Set(members.map(m => m.id));
  const candidates = (users.data ?? []).filter(u => u.isActive && !inSpace.has(u.id));
  const chosen = candidates.find(u => u.id === who);
  const partner = (r: Role) => !can(r, 'activity.own');
  const setLead = (m: Member, r: 'lead' | 'member') => add.mutate({ userId: m.id, role: r }, { onSuccess: () => { done(); toast(`${m.name} is now ${r === 'lead' ? 'a lead' : 'a member'} of ${name}.`); } });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!chosen) return;
    const r = partner(chosen.role) ? 'member' : role;
    add.mutate({ userId: chosen.id, role: r }, { onSuccess: () => { done(); toast(`Added ${chosen.name} to ${name}.`); setWho(''); setRole('member'); } });
  };
  const drop = (m: Member) => remove.mutate(m.id, { onSuccess: () => { done(); setAsking(null); toast(`Removed ${m.name} from ${name}.`); } });

  const list = (
    <div className={phone ? 'ph-grp mbl' : 'mbl'} role="list" aria-label={`${name} members`}>
      {members.map(m => (
        <div className={phone ? 'ph-rowx mbr' : 'lrow mbr'} role="listitem" key={m.id} data-member={m.name}>
          <Avatar p={m} />
          <div className="mbn"><div className="sm">{m.name}</div><div className="xs mut">{ROLE_WORD[m.userRole]}{m.title ? ` · ${m.title}` : ''}</div></div>
          <select className={phone ? 'ph-inp mbs' : 'inp mbs'} aria-label={`${m.name}'s role in ${name}`} value={m.role} disabled={add.isPending || partner(m.userRole)}
            onChange={e => setLead(m, e.target.value as 'lead' | 'member')}>
            {!partner(m.userRole) && <option value="lead">Lead</option>}
            <option value="member">Member</option>
          </select>
          <button type="button" className={phone ? 'ph-btm' : 'bt sm'} onClick={() => setAsking(m.id)} aria-label={`Remove ${m.name}`}>Remove</button>
          {asking === m.id && (
            <div className="cfm mbcfm" role="alert"><span>Remove {m.name} from {name}? They stop seeing it. Activities they own here go to the space lead.</span>
              <button type="button" className={phone ? 'ph-btm pri' : 'bt pri sm'} disabled={remove.isPending} onClick={() => drop(m)}>Remove</button>
              <button type="button" className={phone ? 'ph-btm' : 'bt sm'} onClick={() => setAsking(null)}>Keep</button></div>
          )}
        </div>
      ))}
      {s && !members.length && <div className="xs mut" style={{ padding: 12 }}>Nobody yet. Add the first person below.</div>}
    </div>
  );
  const form = (
    <form className="mbadd" onSubmit={submit} aria-label="Add person">
      <label className="fld">Person
        <select className={phone ? 'ph-inp' : 'inp'} value={who} onChange={e => setWho(e.target.value)} required>
          <option value="">Choose someone</option>
          {candidates.map(u => <option key={u.id} value={u.id}>{u.name} ({ROLE_WORD[u.role]})</option>)}
        </select>
      </label>
      <label className="fld">Role in {name}
        <select className={phone ? 'ph-inp' : 'inp'} value={chosen && partner(chosen.role) ? 'member' : role} disabled={!!chosen && partner(chosen.role)} onChange={e => setRole(e.target.value as 'lead' | 'member')}>
          <option value="member">Member</option><option value="lead">Lead</option>
        </select>
      </label>
      <span className="xs mut">{chosen && partner(chosen.role) ? 'Partners read the space and ask; they join as members.' : 'A lead edits every track\'s context in the space.'}</span>
      <button className={phone ? 'ph-btf' : 'bt pri'} disabled={!chosen || add.isPending}>Add to {name}</button>
    </form>
  );
  const errors = <>{add.error ? <ErrorNote error={add.error} /> : null}{remove.error ? <ErrorNote error={remove.error} /> : null}</>;

  if (phone) return (
    <PSheet label={`${name} members`} onClose={onClose}>
      <h2>Members</h2>
      <div className="f13 mu" style={{ marginTop: -6, marginBottom: 10 }}>{name} · {members.length} {members.length === 1 ? 'person' : 'people'}</div>
      {list}{errors}
      <div className="ph-lbl2">Add person</div>
      {form}
    </PSheet>
  );
  return (
    <Sheet crumbs={<><span>Settings</span><span>/</span><b>{name} members</b></>} onClose={onClose} width={620} label={`${name} members`}>
      <h2 className="stitle" style={{ fontSize: 24 }}>{name} members</h2>
      <span className="xs mut">{members.length} {members.length === 1 ? 'person' : 'people'}. The director and admins see every space without being in it.</span>
      {list}{errors}
      <div className="sh" style={{ marginTop: 6 }}><b>Add person</b></div>
      {form}
    </Sheet>
  );
}
